import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DashboardFolderT, DashboardT } from 'business/definitions';
import {
    ACTIVE_DASHBOARDS_QUERY_KEY,
    OTHER_DASHBOARDS_QUERY_KEY,
} from 'routes/AllDashboards/hooks/getDashboardsList';
import { RQAPICall } from 'store/APIClient';
import { useDispatch } from 'store/hooks';

const deleteFolder = async (id: number): Promise<{ message: string }> => {
    const response = await RQAPICall({
        endpoint: `explore/dashboard_folders/${id}`,
        method: 'DELETE',
    });
    return response.content;
};

/**
 * RQ Mutation to delete a dashboard folder.
 *  * @param {number} folderId - The id of the dashboard folder to delete
 */
export const useDeleteFolder = (folderId: number) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: () => deleteFolder(folderId),

        onSuccess: ({ message }) => {
            dispatch.vision.notifyUser({
                message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                ['dashboard_folders'],
                (oldDashboardFolders: DashboardFolderT[]) =>
                    oldDashboardFolders.filter(
                        (folder) => folder.id !== folderId,
                    ),
            );
            [ACTIVE_DASHBOARDS_QUERY_KEY, OTHER_DASHBOARDS_QUERY_KEY].forEach(
                (key) => {
                    queryClient.setQueryData(
                        [key],
                        (dashboards: DashboardT[] | undefined) =>
                            dashboards?.map((dashboard) => ({
                                ...dashboard,
                                folder:
                                    dashboard?.folder?.id === folderId
                                        ? undefined
                                        : dashboard.folder,
                            })),
                    );
                },
            );
        },
        meta: { useGlobalOnError: true },
    });
};
